import React, { useState } from 'react';
import { PlusCircle, Edit } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import SortableTable from '../../../components/ui/SortableTable';
import CompanyForm from '../forms/CompanyForm';
import ApiService from '../../../services/apiService';
import CurrencyService from '../../../services/currencyService';
import formatDate from '../utils/dateFormatter';

const CompaniesPage = ({ companies, onDataRefresh, addMessage, selectedCompany, setSelectedCompany, userRole, canCreate, onCompanyCreated }) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);

    const handleCreateCompany = async (data) => {
        try {
            const result = await ApiService.createCompany(data);
            // Call onCompanyCreated if provided to set up permissions
            if (onCompanyCreated && result && result[0]) {
                await onCompanyCreated(result[0]);
            }
            onDataRefresh();
            setShowCreateModal(false);
            addMessage('Company created successfully!', 'success');
        } catch (error) {
            addMessage(`Error creating company: ${error.message}`, 'error');
        }
    };

    const handleUpdateCompany = async (id, data) => {
        try {
            await ApiService.updateCompany(id, data);
            onDataRefresh();
            setShowEditModal(false);
            setEditingCompany(null);
            addMessage('Company updated successfully!', 'success');
        } catch (error) {
            addMessage(`Error updating company: ${error.message}`, 'error');
        }
    };

    const handleEditClick = (company) => {
        setEditingCompany(company);
        setShowEditModal(true);
    };

    const handleSelectCompany = (company) => {
        if (typeof setSelectedCompany === 'function') {
            setSelectedCompany(company);
        } else {
            console.error('setSelectedCompany is not a function:', setSelectedCompany);
            addMessage('Error selecting company. Please refresh the page.', 'error');
        }
    };

    const companiesTableColumns = [
        { key: 'name', header: 'Company Name', isSortable: true },
        { 
            key: 'description', 
            header: 'Description', 
            isSortable: true,
            render: (row) => {
                const desc = row.description || '';
                return desc.length > 50 ? desc.substring(0, 47) + '...' : desc || '-';
            }
        },
        { 
            key: 'currency', 
            header: 'Currency', 
            isSortable: true,
            render: (row) => {
                const currency = row.currency || 'USD';
                const currencyInfo = CurrencyService.getCurrencyByCode(currency);
                return (
                    <span className="flex items-center space-x-1">
                        <span>{currencyInfo?.flag || '🌍'}</span>
                        <span>{currency}</span>
                    </span>
                );
            }
        },
        { 
            key: 'created_at', 
            header: 'Created', 
            isSortable: true,
            render: (row) => formatDate(row.created_at)
        },
        { 
            key: 'actions', 
            header: 'Actions', 
            isSortable: false,
            render: (row) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleSelectCompany(row)}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            selectedCompany?.id === row.id
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                    >
                        {selectedCompany?.id === row.id ? 'Selected' : 'Select'}
                    </button>
                    {userRole !== 'viewer' && (
                        <button
                            onClick={() => handleEditClick(row)}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Companies</h2>
                {canCreate && (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <PlusCircle className="h-5 w-5 mr-2" />
                        Add Company
                    </button>
                )}
            </div>

            <SortableTable
                data={companies}
                columns={companiesTableColumns}
                entityType="company"
                addMessage={addMessage}
            />

            {showCreateModal && (
                <Modal onClose={() => setShowCreateModal(false)}>
                    <CompanyForm
                        onSubmit={handleCreateCompany}
                        onCancel={() => setShowCreateModal(false)}
                    />
                </Modal>
            )}

            {showEditModal && editingCompany && (
                 <Modal onClose={() => setShowEditModal(false)}>
                    <CompanyForm
                        initialData={editingCompany}
                        onSubmit={(data) => handleUpdateCompany(editingCompany.id, data)}
                        onCancel={() => {
                            setShowEditModal(false);
                            setEditingCompany(null);
                        }}
                    />
                </Modal>
            )}
        </div>
    );
};

export default CompaniesPage;